import { useMemo } from 'react';
import type { WeatherType, BiomeType } from '../../types';
import { RainEffect } from './RainEffect';
import { SnowEffect } from './SnowEffect';
import { SandstormEffect } from './SandstormEffect';
import { FogEffect } from './FogEffect';
import { BubblesEffect } from './BubblesEffect';
import { DrizzleEffect } from './DrizzleEffect';
import { CausticsEffect } from './CausticsEffect';
import { SporesEffect } from './SporesEffect';
import { LightningEffect } from './LightningEffect';
import { AnimatedNeonEffect, UpsideDownNeonText } from './AnimatedNeonEffect';
import { CloudsEffect } from './CloudsEffect';

interface WeatherSystemProps {
  weatherType: WeatherType;
  intensity?: number;
  areaSize: [number, number];
  biome?: BiomeType;
  world?: 'real' | 'spectral';
  onLightningStrike?: () => void;
  onNeonBlink?: () => void;
  onRainDrop?: () => void;
  // Position du joueur pour les éclairs effrayants
  playerPosition?: [number, number, number];
}

// Biome-specific weather configurations
const BIOME_WEATHER_CONFIG: Record<BiomeType, {
  defaultWeather: WeatherType;
  weatherColor: string;
  fogColor: string;
  intensity: number;
}> = {
  forest: {
    defaultWeather: 'drizzle',
    weatherColor: '#a0c0e0',
    fogColor: '#90a090',
    intensity: 0.3,
  },
  jungle: {
    defaultWeather: 'rain',
    weatherColor: '#80b0d0',
    fogColor: '#608060',
    intensity: 0.6,
  },
  desert: {
    defaultWeather: 'sandstorm',
    weatherColor: '#d4a574',
    fogColor: '#c0a070',
    intensity: 0.5,
  },
  mountain: {
    defaultWeather: 'snow',
    weatherColor: '#ffffff',
    fogColor: '#c0c8d0',
    intensity: 0.4,
  },
  marine: {
    defaultWeather: 'bubbles',
    weatherColor: '#80d0ff',
    fogColor: '#4080a0',
    intensity: 0.7,
  },
  tundra: {
    defaultWeather: 'blizzard',
    weatherColor: '#e8f0ff',
    fogColor: '#a0b0c0',
    intensity: 0.7,
  },
  swamp: {
    defaultWeather: 'fog',
    weatherColor: '#90a080',
    fogColor: '#607050',
    intensity: 0.6,
  },
  celestial: {
    defaultWeather: 'none',
    weatherColor: '#d0b0ff',
    fogColor: '#8060a0',
    intensity: 0.3,
  },
  upside_down: {
    defaultWeather: 'spores',
    weatherColor: '#4a2060', // Violet sombre
    fogColor: '#0d1a2d', // Bleu très sombre
    intensity: 0.8,
  },
  suburban: {
    defaultWeather: 'none',
    weatherColor: '#87CEEB', // Bleu ciel
    fogColor: '#e8e8e8',
    intensity: 0.2,
  },
  ice: {
    defaultWeather: 'snow',
    weatherColor: '#ffffff', // Blanc neige
    fogColor: '#c8d8e8',
    intensity: 0.5,
  },
};

export function WeatherSystem({
  weatherType,
  intensity: propIntensity,
  areaSize,
  biome = 'forest',
  world = 'real',
  onLightningStrike,
  onNeonBlink,
  onRainDrop,
  playerPosition,
}: WeatherSystemProps) {
  const config = BIOME_WEATHER_CONFIG[biome];
  const effectiveWeather = weatherType === 'none' ? config.defaultWeather : weatherType;
  const intensity = propIntensity ?? config.intensity;
  const isSpectral = world === 'spectral';

  // Center position for the weather area
  const centerOffset: [number, number] = useMemo(() => [
    areaSize[0] / 2,
    areaSize[1] / 2,
  ], [areaSize]);

  if (effectiveWeather === 'none') {
    return null;
  }

  return (
    <group position={[centerOffset[0], 0, centerOffset[1]]}>
      {/* Rain effect */}
      {effectiveWeather === 'rain' && (
        <RainEffect
          intensity={intensity}
          particleCount={800}
          areaSize={areaSize}
          color={config.weatherColor}
          windStrength={0.3}
          onDrop={onRainDrop}
        />
      )}

      {/* Drizzle effect - lighter rain */}
      {effectiveWeather === 'drizzle' && (
        <DrizzleEffect
          intensity={intensity}
          particleCount={300}
          areaSize={areaSize}
          color={config.weatherColor}
        />
      )}

      {/* Snow effect */}
      {(effectiveWeather === 'snow' || effectiveWeather === 'blizzard') && (
        <SnowEffect
          intensity={intensity}
          particleCount={effectiveWeather === 'blizzard' ? 800 : 400}
          areaSize={areaSize}
          color={config.weatherColor}
          isBlizzard={effectiveWeather === 'blizzard'}
        />
      )}

      {/* Sandstorm effect */}
      {effectiveWeather === 'sandstorm' && (
        <SandstormEffect
          intensity={intensity}
          particleCount={600}
          areaSize={areaSize}
          color={config.weatherColor}
        />
      )}

      {/* Fog effect - can be combined with other weather */}
      {(effectiveWeather === 'fog' || biome === 'swamp' || biome === 'jungle') && (
        <FogEffect
          intensity={effectiveWeather === 'fog' ? intensity : intensity * 0.4}
          areaSize={areaSize}
          color={config.fogColor}
          height={effectiveWeather === 'fog' ? 3 : 1.5}
        />
      )}

      {/* Underwater bubbles */}
      {effectiveWeather === 'bubbles' && (
        <BubblesEffect
          intensity={intensity}
          particleCount={80}
          areaSize={areaSize}
          color={config.weatherColor}
          maxHeight={8}
        />
      )}

      {/* Caustics effect for underwater */}
      {biome === 'marine' && (
        <CausticsEffect
          intensity={intensity * 0.6}
          areaSize={areaSize}
          color={config.weatherColor}
          speed={0.8}
        />
      )}

      {/* Additional atmospheric fog for blizzard */}
      {effectiveWeather === 'blizzard' && (
        <FogEffect
          intensity={intensity * 0.5}
          areaSize={areaSize}
          color="#d0e0f0"
          height={4}
        />
      )}

      {/* Spores/Petals effect for upside_down - only in spectral world */}
      {biome === 'upside_down' && isSpectral && (
        <SporesEffect
          intensity={intensity * 0.7}
          particleCount={50}
          areaSize={areaSize}
        />
      )}

      {/* Lightning effect for upside_down - only in spectral world */}
      {biome === 'upside_down' && isSpectral && (
        <LightningEffect
          intensity={intensity}
          areaSize={areaSize}
          color="blue"
          minInterval={3000}
          maxInterval={8000}
          onStrike={onLightningStrike}
          playerPosition={playerPosition}
          strikeRadius={4}
        />
      )}

      {/* Dense fog for upside_down atmosphere - only in spectral world */}
      {biome === 'upside_down' && isSpectral && (
        <FogEffect
          intensity={intensity * 0.5}
          areaSize={areaSize}
          color={config.fogColor}
          height={4}
          layerCount={5}
        />
      )}

      {/* Menacing clouds for upside_down - spectral world */}
      {biome === 'upside_down' && isSpectral && (
        <CloudsEffect
          intensity={0.7}
          cloudCount={10}
          areaSize={areaSize}
          height={10}
          speed={0.3}
          world="spectral"
        />
      )}

      {/* Normal clouds for suburban real world */}
      {biome === 'suburban' && !isSpectral && (
        <CloudsEffect
          intensity={0.5}
          cloudCount={12}
          areaSize={areaSize}
          height={12}
          speed={0.2}
          world="real"
        />
      )}

      {/* Clouds for upside_down real world (before transition) */}
      {biome === 'upside_down' && !isSpectral && (
        <CloudsEffect
          intensity={0.6}
          cloudCount={10}
          areaSize={areaSize}
          color="#8090a0"
          height={11}
          speed={0.25}
          world="real"
        />
      )}

      {/* Animated neon bars for upside_down - only in spectral world */}
      {biome === 'upside_down' && isSpectral && (
        <>
          <AnimatedNeonEffect
            bars={[
              // Large vertical neon bars on the walls
              { position: [-areaSize[0] / 2 + 1, 3, -2], rotation: [0, 0, 0], length: 4, thickness: 0.12 },
              { position: [-areaSize[0] / 2 + 1, 3, 5], rotation: [0, 0, 0], length: 3.5, thickness: 0.1 },
              { position: [areaSize[0] / 2 - 1, 2.5, 3], rotation: [0, 0, 0], length: 3, thickness: 0.1 },
              { position: [areaSize[0] / 2 - 1, 3.5, -4], rotation: [0, 0, 0], length: 4.5, thickness: 0.12 },
              // Horizontal bars
              { position: [0, 4, -areaSize[1] / 2 + 1], rotation: [0, 0, Math.PI / 2], length: 5, thickness: 0.1 },
              { position: [3, 2, -areaSize[1] / 2 + 1], rotation: [0, 0, Math.PI / 2], length: 3, thickness: 0.08 },
              // Diagonal bars
              { position: [-4, 3, areaSize[1] / 2 - 2], rotation: [0, 0, Math.PI / 6], length: 2.5, thickness: 0.08 },
              { position: [5, 2.5, areaSize[1] / 2 - 2], rotation: [0, 0, -Math.PI / 5], length: 2, thickness: 0.08 },
            ]}
            blinkSpeed={2.5}
            colorChangeSpeed={0.6}
            intensity={1}
            onBlink={onNeonBlink}
          />

          {/* "UPSIDE DOWN" neon text on wall */}
          <UpsideDownNeonText
            position={[0, 4, -areaSize[1] / 2 + 0.5]}
            rotation={[0, 0, 0]}
            scale={1.5}
            blinkSpeed={1.2}
          />
        </>
      )}
    </group>
  );
}

export { RainEffect } from './RainEffect';
export { SnowEffect } from './SnowEffect';
export { SandstormEffect } from './SandstormEffect';
export { FogEffect } from './FogEffect';
export { BubblesEffect } from './BubblesEffect';
export { DrizzleEffect } from './DrizzleEffect';
export { CausticsEffect } from './CausticsEffect';
export { SporesEffect } from './SporesEffect';
export { LightningEffect } from './LightningEffect';
export { AnimatedNeonEffect, UpsideDownNeonText } from './AnimatedNeonEffect';
export { CloudsEffect } from './CloudsEffect';
